import { Error, Info } from "@mui/icons-material";
import { Box, Collapse, TextField, Tooltip, Typography } from "@mui/material";
import { FocusEventHandler, KeyboardEventHandler } from "react";
import { ErrorMessage } from "../../../../App/Components/Forms/formHelpers";
import {
  useMessageInputHandlers,
  useMessageInputState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageInput";
import { maxTextOverallLength } from "../../../Constants/MessageConstants";

export default function TextMessageInput({
  handleFocus,
  handleKeyboardInput,
  disabledInput = false,
  RegulationTextComponent,
  errorMessage,
}: {
  handleFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleKeyboardInput?: KeyboardEventHandler<HTMLDivElement>;
  disabledInput?: boolean;
  RegulationTextComponent?: JSX.Element;
  errorMessage?: string;
}) {
  const { body, bodyErrorMessage, textSplitCount, textSplitWarning, textContainsLink, touched } =
    useMessageInputState();
  const { handleInputBodyChange } = useMessageInputHandlers();
  return (
    <>
      <TextField
        multiline
        fullWidth
        aria-describedby="text-input-error"
        id="inputSendTextMessage"
        name="inputSendTextMessage"
        placeholder="Send a message"
        minRows={3}
        onKeyDown={handleKeyboardInput}
        onChange={handleInputBodyChange}
        onFocus={handleFocus}
        value={body}
        disabled={disabledInput}
        error={touched && (!!bodyErrorMessage || !!errorMessage)}
      />
      <TextMessageErrorMessaging
        inputTextMessage={body}
        bodyErrorMessage={touched ? (bodyErrorMessage ?? errorMessage) : undefined}
        textSplitWarning={textSplitWarning}
        textSplitCount={textSplitCount}
        textContainsLinkMessage={textContainsLink}
        RegulationTextComponent={RegulationTextComponent}
      />
    </>
  );
}

export function TextMessageErrorMessaging({
  inputTextMessage,
  bodyErrorMessage,
  textSplitWarning,
  textSplitCount,
  textContainsLinkMessage,
  RegulationTextComponent,
  customErrorMessage,
}: {
  inputTextMessage: string;
  bodyErrorMessage?: string;
  textSplitWarning?: string;
  textSplitCount?: number;
  textContainsLinkMessage?: string;
  RegulationTextComponent?: JSX.Element;
  customErrorMessage?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexDirection: "row",
        alignItems: "flex-end",
      }}
    >
      <Collapse in={!!bodyErrorMessage} unmountOnExit>
        <ErrorMessage message={bodyErrorMessage || ""} id="body-error-message" />
      </Collapse>
      {RegulationTextComponent}
      <Typography
        variant="caption"
        sx={{
          fontStyle: "italic",
          color: bodyErrorMessage ? "error.main" : "primary",
          ml: { xs: 0, md: RegulationTextComponent ? 0 : "auto" },
          display: "flex",
        }}
      >
        {inputTextMessage.trim().length}/{maxTextOverallLength}
      </Typography>
      {textSplitWarning && (
        <Tooltip title={textSplitWarning}>
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              gap: "0.25em",
              alignItems: "center",
              fontStyle: "italic",
              color: "warning.main",
            }}
          >
            {textSplitCount} SMS <Error color="error" />
          </Typography>
        </Tooltip>
      )}
      {textContainsLinkMessage && (
        <Tooltip title={textContainsLinkMessage} placement="top">
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              gap: "0.25em",
              alignItems: "center",
              fontStyle: "italic",
              color: "warning.main",
            }}
          >
            <Info color="secondary" />
          </Typography>
        </Tooltip>
      )}
      {customErrorMessage && (
        <Tooltip title={customErrorMessage} placement="top">
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              gap: "0.25em",
              alignItems: "center",
              fontStyle: "italic",
              color: "error.main",
            }}
          >
            <Error color="error" />
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}
